import { useLikedBrandIds } from '@aboutyou/legacy-shared'
import { createFilterSlug, readIdFromAysaSlug } from 'helpers/aysaSlugs'
import { exists } from 'helpers/exists'
import { useTranslation } from 'helpers/hooks/useTranslation'
import React, { ChangeEvent, FC, useCallback, useRef } from 'react'
import { useRect } from 'react-viewport-utils'
import { List, ListRowRenderer } from 'react-virtualized/dist/commonjs/List'
import { SlugifiedAttributesFilterItemWithValues } from 'services/providers/bapi/entities/filters/normalize'
import BrandFilterButton from 'sharedComponents/ProductStream/filters/BrandFilterButton'
import { useAttributesFilterProps } from 'sharedComponents/ProductStream/filters/useAttributesFilterProps'
import Brand from 'store/entities/Brands/Brand.store'
import styled, { css } from 'styled-components'
import { Option } from 'types/option'
import Icon, { IconSize } from 'uiComponents/atoms/Icon/Icon'
import { IconName } from 'uiComponents/atoms/Icon/IconName'
import { HeadingXS } from 'uiComponents/atoms/Text/Headings'
import TextInput from 'uiComponents/atoms/TextInput/TextInput'
import Toggle from 'uiComponents/atoms/Toggle/Toggle'
import {
  bordersColor,
  complementaryColor,
  darkGrayColor,
  primaryColor,
} from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsXS,
  fsXXS,
  fwBold,
  fwMediumBold,
} from 'uiComponents/constants/typography'
import { useFocusInside } from 'uiComponents/helpers/useFocusInside'
import { useLargeListFilterOptions } from 'uiComponents/helpers/useLargeListFilterOptions'
import { useLikedBrandLoader } from 'uiComponents/helpers/useLikedBrandLoader'
import DropdownBox from 'uiComponents/molecules/Dropdown/DropdownBox'
import DropdownOption from 'uiComponents/molecules/Dropdown/DropdownOptions/OptionContainer'
import UnfoldingFilterBox from 'uiComponents/organisms/Filters/UnfoldingFilterBox'
import {
  useTrackOnCloseFilter,
  useTrackOnOpenFilter,
} from '../../../../helpers/tracking/hooks/trackFilter'

const LIST_ROW_HEIGHT = 32
const LIST_MAX_HEIGHT = 275
// where 40 is the height of the bottom all liked brands toggle
const LIST_MAX_HEIGHT_WITH_LIKED_BRANDS = LIST_MAX_HEIGHT - 40
const COLUMNS_AMOUNT_FOR_BRANDS_LIST = 1
const SUPPORT_KEY_EVENTS = true

const StyledHeadingS = styled(HeadingXS)`
  font-size: ${fsXS}px;
  ${fwBold}
  margin: 24px 0 8px;
`

const StyledBrandFilterButton = styled(BrandFilterButton)`
  padding: 8px;
`

const LikedBrandsWrapper = styled.div`
  flex: 1;
`

const FilterButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  max-height: ${LIST_MAX_HEIGHT_WITH_LIKED_BRANDS}px;
  flex: 1;
  padding-bottom: 16px;
`

const StyledTextInput = styled(TextInput)`
  width: 100%;

  .MuiInputBase-input {
    height: 16px;
    text-align: left;
  }

  .MuiInputLabel-root:not(.Mui-focused) {
    left: 0;
    transform: translate(14px, 20px) scale(1);
  }

  .mdc-text-field__input {
    padding-right: 48px;
  }
`

const StyledIcon = styled(Icon)`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  pointer-events: none;
`

const StyledList = styled(List)`
  padding: 0;
  &:focus {
    outline: none;
  }
  li {
    padding: 0;
    font-size: ${fsXXS}px;
    color: ${darkGrayColor};
    white-space: nowrap;
    flex: ${100 / COLUMNS_AMOUNT_FOR_BRANDS_LIST}%;
    max-width: ${100 / COLUMNS_AMOUNT_FOR_BRANDS_LIST}%;
  }
`

const StyledListRow = styled.ul`
  display: flex;
  flex-wrap: wrap;
`

const StyledDropdownBox = styled(DropdownBox)`
  left: 0;
  top: 100%;
  max-height: 100%;
  position: relative;
  margin-top: 16px;
  box-shadow: none;
  overflow: hidden;

  & > .ReactVirtualized__List {
    direction: inherit !important;
  }
`

const Wrapper = styled.div<{ isOpen: boolean; areLikedBrands: boolean }>`
  position: relative;
  z-index: 0;
  box-sizing: border-box;
  ${({ isOpen, areLikedBrands }) =>
    !areLikedBrands &&
    !isOpen &&
    css`
      padding-bottom: 16px;
    `}
`

const InputAndLikedBrandsHolder = styled.div`
  box-sizing: border-box;
`

const WrapperRefHolder = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StyledToggle = styled(Toggle)`
  ${fontFamilyPrimary};
  ${fwMediumBold};
  font-size: ${fsXS}px;
  color: ${primaryColor};
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
  padding: 16px 24px;
  min-height: 40px;
  box-shadow: 0px -16px 16px -10px ${complementaryColor};
  border-top: 1px solid ${bordersColor};
`

interface AllFavoriteBrandsToggleProps {
  slug: string
  checked: boolean
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  label: string
}

interface LikedBrandsProps {
  likedBrands: Brand[]
  isOpen: boolean
  toggleSelection: (brand: Brand) => void
  isActive: (brandId: string) => boolean
}

const AllFavoriteBrandsToggle: FC<AllFavoriteBrandsToggleProps> = ({
  checked,
  onChange,
  label,
}) => {
  const { t } = useTranslation()

  return <StyledToggle checked={checked} onChange={onChange} label={t(label)} />
}

const LikedBrands: FC<LikedBrandsProps> = ({
  likedBrands,
  isOpen,
  toggleSelection,
  isActive,
}) => {
  const { t } = useTranslation()

  if (isOpen || likedBrands.length === 0) {
    return null
  }

  return (
    <LikedBrandsWrapper>
      <StyledHeadingS>{t('Deine Lieblingsmarken')}</StyledHeadingS>
      <FilterButtons>
        {likedBrands.map((brand: Brand) => (
          <StyledBrandFilterButton
            key={brand.id}
            brand={brand}
            onClick={() => toggleSelection(brand)}
            active={isActive(brand.id)}
          />
        ))}
      </FilterButtons>
    </LikedBrandsWrapper>
  )
}

const BrandFilter: FC<{
  filter: SlugifiedAttributesFilterItemWithValues
}> = ({ filter }) => {
  const { t } = useTranslation()
  const props = useAttributesFilterProps(filter)
  const likedBrandIds = useLikedBrandIds()

  const onCloseFilterTracking = useTrackOnCloseFilter()
  const onOpenFilterTracking = useTrackOnOpenFilter()
  const trackOnClose = () => {
    onCloseFilterTracking('brand', 'quick_filters')
  }
  const trackOnOpen = () => {
    onOpenFilterTracking('brand', 'quick_filters')
  }

  const availableLikedBrandIds = likedBrandIds
    .map(String)
    .filter(brandId =>
      exists(
        props.options.find(
          option => readIdFromAysaSlug(option.id).toString() === brandId,
        ),
      ),
    )

  const likedBrands = useLikedBrandLoader(availableLikedBrandIds)

  const allLikedBrandsSelected = likedBrands.every(option =>
    isActive(option.id),
  )

  function toggleAllLikedBrands() {
    const optionsToToggle = allLikedBrandsSelected
      ? likedBrands
      : likedBrands.filter(option => !isActive(option.id))

    optionsToToggle.forEach(option => toggleSelection(option))
  }

  function isActive(brandId: string) {
    const selectedVal = props.selectedValues.find(
      value => readIdFromAysaSlug(value).toString() === brandId,
    )

    return exists(selectedVal)
  }

  function toggleSelection(brand: Brand) {
    const slug = createFilterSlug({ id: brand.id, name: brand.name })

    props.toggleSelection(slug)
  }

  const wrapperRef = useRef<HTMLDivElement>(null)
  const dropDownRef = useRef<HTMLDivElement>(null)
  const rect = useRect(wrapperRef)
  const { focused: isOpen, ...focusInside } = useFocusInside(
    [wrapperRef, dropDownRef],
    SUPPORT_KEY_EVENTS,
  )

  const isOptionSelected = useCallback(
    (option: Option) => {
      return props.selectedValues.includes(option.id)
    },
    [props.options, props.selectedValues],
  )

  const { filteredOptions, searchTerm, setSearchTerm } =
    useLargeListFilterOptions({
      isOpen,
      options: props.options,
      selectedValues: props.selectedValues,
    })

  const onToggle = (id: string) => {
    props.toggleSelection(id)
  }

  const rowRenderer = useCallback<ListRowRenderer>(
    ({ index, key, style }) => {
      const items = []
      const fromIndex = index * COLUMNS_AMOUNT_FOR_BRANDS_LIST
      const toIndex = Math.min(
        fromIndex + COLUMNS_AMOUNT_FOR_BRANDS_LIST,
        filteredOptions.length,
      )

      for (let i = fromIndex; i < toIndex; i++) {
        const option = filteredOptions[i]

        items.push(
          <DropdownOption
            onClick={() => onToggle(option.id)}
            key={option.id}
            withCheckbox
            selected={isOptionSelected(option)}
          >
            {option.name}
          </DropdownOption>,
        )
      }

      return (
        <StyledListRow key={key} style={style}>
          {items}
        </StyledListRow>
      )
    },
    [filteredOptions, props.selectedValues],
  )

  const rowCount = Math.round(
    filteredOptions.length / COLUMNS_AMOUNT_FOR_BRANDS_LIST,
  )

  return (
    <UnfoldingFilterBox
      dataTestId="Brand"
      label={filter.name}
      activeFiltersCount={props.selectedValues.length}
      onReset={props.onReset}
      styledContentStyles={{ padding: '16px 24px 0' }}
      settings={
        likedBrands.length > 0
          ? [
              <AllFavoriteBrandsToggle
                key="all-favorite-brands"
                label={t('Nach allen Lieblingsmarken filtern')}
                slug={filter.slug}
                checked={allLikedBrandsSelected}
                onChange={toggleAllLikedBrands}
              />,
            ]
          : undefined
      }
      onClose={trackOnClose}
      onOpen={trackOnOpen}
    >
      <Wrapper
        data-testid="LargeListFilterWrapper"
        isOpen={isOpen}
        areLikedBrands={Boolean(likedBrandIds.length)}
      >
        <InputAndLikedBrandsHolder>
          <WrapperRefHolder ref={wrapperRef} {...focusInside}>
            <StyledTextInput
              onChange={e => setSearchTerm(e.target.value)}
              label={t('Nach Marke suchen')}
              value={searchTerm}
            >
              <StyledIcon asset={IconName.IcSearch} size={IconSize.S} />
            </StyledTextInput>
            {isOpen && (
              <StyledDropdownBox ref={dropDownRef}>
                <StyledList
                  height={300}
                  width={rect?.width || 320}
                  rowCount={rowCount}
                  rowHeight={LIST_ROW_HEIGHT}
                  rowRenderer={rowRenderer}
                  overscanRowCount={0}
                  filteredOptions={filteredOptions}
                  selectedValues={props.selectedValues}
                />
              </StyledDropdownBox>
            )}
          </WrapperRefHolder>
          <LikedBrands
            likedBrands={likedBrands}
            isOpen={isOpen}
            toggleSelection={toggleSelection}
            isActive={isActive}
          />
        </InputAndLikedBrandsHolder>
      </Wrapper>
    </UnfoldingFilterBox>
  )
}

export default BrandFilter
